import { fetchWithAuth, API_BASE } from "@/lib/auth";

const BASE = `${API_BASE}/api/v1/policies`;

async function req(path: string, options: RequestInit = {}) {
    const res = await fetchWithAuth(`${BASE}${path}`, {
        ...options,
        headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Request failed (${res.status})`);
    }
    const ct = res.headers.get("content-type") || "";
    return ct.includes("application/json") ? res.json() : res.text();
}

// ── Orientation / glossary ─────────────────────────────────────────────
export const getGlossary = () => req("/glossary");
export const getOrientation = () => req("/orientation");

// ── Member / steward / answerer ────────────────────────────────────────
export const getMyAssignments = () => req("/me/assignments");
export const getMyQuestions = () => req("/me/questions");
export const getWorkspace = (policyId: string) => req(`/workspace/${policyId}`);
export const saveSuggestion = (policyId: string, sid: string, body: any) =>
    req(`/workspace/${policyId}/statements/${sid}/suggestion`, { method: "PUT", body: JSON.stringify(body) });
export const saveAnswer = (policyId: string, pId: string, body: any) =>
    req(`/workspace/${policyId}/questions/${pId}/answer`, { method: "PUT", body: JSON.stringify(body) });
export const addAddition = (policyId: string, body: string) =>
    req(`/workspace/${policyId}/additions`, { method: "POST", body: JSON.stringify({ body }) });
export const acknowledgeFlag = (policyId: string, flagId: string) =>
    req(`/workspace/${policyId}/flags/${flagId}/ack`, { method: "POST" });
export const resolveFlagSuggestion = (policyId: string, sugId: string, note: string) =>
    req(`/workspace/${policyId}/suggestions/${sugId}/resolve-flag`, { method: "POST", body: JSON.stringify({ note }) });
export const submitPolicy = (policyId: string) =>
    req(`/workspace/${policyId}/submit`, { method: "POST" });

// ── Ratified library ────────────────────────────────────────────────────
export const getLibrary = (params: { domain?: string; q?: string } = {}) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return req(`/library${qs ? `?${qs}` : ""}`);
};
export const getLibraryDetail = (policyId: string) => req(`/library/${policyId}`);

// ── Public Policy Centre (no auth — safe for logged-out visitors) ────────
async function publicReq(path: string) {
    const res = await fetch(`${BASE}${path}`);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Request failed (${res.status})`);
    }
    return res.json();
}
export const getPublicLibrary = (params: { domain?: string; q?: string } = {}) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return publicReq(`/public/library${qs ? `?${qs}` : ""}`);
};
export const getPublicLibraryDetail = (policyId: string) => publicReq(`/public/library/${policyId}`);

// ── Group dashboard ──────────────────────────────────────────────────────
export const getGroupPolicies = (entityType: string, entityId: string | number) =>
    req(`/group/${entityType}/${entityId}`);
export const getPolicyReadView = (policyId: string) => req(`/view/${policyId}`);

// ── Admin console ────────────────────────────────────────────────────────
export const getConsoleAccess = () => req("/console/access");
export const getBoard = () => req("/console/board");
export const assignSteward = (policyId: string, body: any) =>
    req(`/console/${policyId}/assign`, { method: "POST", body: JSON.stringify(body) });
export const ratifySteward = (policyId: string) =>
    req(`/console/${policyId}/ratify-steward`, { method: "POST" });
export const transitionPolicy = (policyId: string, targetState: string, reason?: string) =>
    req(`/console/${policyId}/transition`, { method: "POST", body: JSON.stringify({ target_state: targetState, reason }) });
export const getCollationQueue = (policyId: string) => req(`/console/${policyId}/collation`);
export const decideItem = (kind: string, itemId: string, decision: string, notice?: string) =>
    req(`/console/collation/${kind}/${itemId}/decide`, { method: "POST", body: JSON.stringify({ decision, notice }) });
export const applyAccepted = (policyId: string) => req(`/console/${policyId}/apply`, { method: "POST" });
export const exportPolicy = (policyId: string, fmt: "json" | "md" = "md") => req(`/console/${policyId}/export?fmt=${fmt}`);
export const resolveFlag = (flagId: string, resolved = true) =>
    req(`/console/flags/${flagId}/resolve`, { method: "POST", body: JSON.stringify({ resolved }) });
export const searchUsers = (q: string) => req(`/console/users/search?q=${encodeURIComponent(q)}`);
export const getConsoleGroups = () => req(`/console/groups`);
export const getGroupLeaders = (entityType: string, entityId: string | number) =>
    req(`/console/groups/${entityType}/${entityId}/leaders`);
export const getGovernance = (policyId: string) => req(`/console/${policyId}/governance`);
export const setApprovalRoute = (policyId: string, route: string | null, reason?: string) =>
    req(`/console/${policyId}/route`, { method: "POST", body: JSON.stringify({ approval_route: route, reason }) });
export const setBoardInvolvement = (policyId: string, involvement: string | null, reason?: string) =>
    req(`/console/${policyId}/board-involvement`, { method: "POST", body: JSON.stringify({ board_involvement: involvement, reason }) });
export const getBoardPacket = () => req(`/console/board-packet`);
export const getPolicyJourney = (policyId: string) => req(`/console/${policyId}/journey`);
export const approveGate = (policyId: string, gate: string, note?: string) =>
    req(`/console/${policyId}/gates/${gate}/approve`, { method: "POST", body: JSON.stringify({ note }) });
export const revokeGate = (policyId: string, gate: string) =>
    req(`/console/${policyId}/gates/${gate}/approve`, { method: "DELETE" });
export const addAdvisor = (policyId: string, userId: string, note?: string) =>
    req(`/console/${policyId}/advisors`, { method: "POST", body: JSON.stringify({ user_id: userId, note }) });
export const removeAdvisor = (policyId: string, advisorId: string) =>
    req(`/console/${policyId}/advisors/${advisorId}`, { method: "DELETE" });
